import React, { Fragment } from 'react';
import { useSelector } from 'react-redux';

import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import { selectors } from '../../../store/CurrentUser.model';

import { requireLogin } from '../../shared/login/SignUpLoginModal';
import { addQueryStringParams } from '../../../modules/url';
import { openExternalUrl } from '../../../modules/openExternalUrl';

import {
  useAndAssignTest,
  useLinkTo,
  useResponsiveEffect,
} from '../../../hooks';
import type { RootState } from '../../../store/models';
import { COM_966_TEST_NAME, COM_966_TEST_PERCENT } from './constants';
import { Button } from '../../ui';
import { AuthOrigins } from '../../../modules/user/constants';
import nonCriticalException from '../../../modules/exceptionLogger';
import { PRO_TODAY_TAB_HOME } from '../../../route-names';
import { SUPPORT_PAGE_URL } from '../../../modules/SupportServiceWrapper';

/**
 * HeaderLinks - this component renders the static links that appear in the client header
 *
 * Usage:
 * ```tsx
 * <HeaderLinksFunctional />
 * ```
 *
 * When the client user is logged in, the only link displayed will be Help
 * When the user is anonymous this bar will include links for Setup My Business,
 * Sign Up, and Log In in addition to the Help link.
 *
 * This component is connected to the redux store, and does not need any props passed
 * explicitly.
 *
 */

export const HeaderLinks: React.FCWithChildren = () => {
  const isLoggedIn: boolean = useSelector(selectors.isLoggedIn);
  const providerId: number = useSelector(selectors.getLoggedInProviderId);
  const stateName: string = useSelector((state: RootState) => state.route.name);
  const utmParams = useSelector((state: RootState) => state.route.params);
  const linkTo = useLinkTo();
  const is = useResponsiveEffect();
  const isInCom966Test = useAndAssignTest(COM_966_TEST_NAME, COM_966_TEST_PERCENT);

  /**
   * Opens the user signup modal
   */
  const promptUserForLoginOrSignup = async (mode?: 'signup' | 'login') => {
    try {
      const { userData } = await requireLogin({
        mode: mode || 'login',
        origin: AuthOrigins.HeaderNav,
        useNewExperience: isInCom966Test,
      });
      if (userData && userData.provider_id) {
        linkTo(PRO_TODAY_TAB_HOME, { providerId: userData.provider_id });
      }
    } catch (e) {
      nonCriticalException(e);
    }
  };

  let showBusinessSetup = true;

  if (typeof stateName === 'string' && stateName.startsWith('provider_signup')) {
    showBusinessSetup = false;
  }

  const setupMyBusinessText = (is.desktop && !is.wide) ? 'Set Up My\nBusiness' : 'Set Up My Business';

  const buttonStyle = {
    color: BrandColor.BackInBlack,
    margin: 'auto',
    fontWeight: 700,
    background: '#fff',
    whitSpace: 'nowrap',
    fontFamily: '',
    fontSize: 18,
  };

  const setUpMyBusinessStyle = {
    color: BrandColor.FloraDark,
    margin: 'auto',
    fontWeight: 700,
    background: '#fff',
    whitSpace: 'nowrap',
    fontFamily: '',
    fontSize: 18,
  };

  const handleSetUpMyBusinessClicked = () => {
    openExternalUrl(addQueryStringParams('/join', {
      source: 'header_for_business',
      ...utmParams,
    }), '_self');
  };

  const handleHelpClicked = () => {
    openExternalUrl(addQueryStringParams(SUPPORT_PAGE_URL, utmParams));
  };

  return (
    <Inline testID="header-links-parent">
      {!isLoggedIn && (
        <Fragment>
          {showBusinessSetup && (
            <Box>
              <Button
                testID="header-link-setup-my-business-button"
                style={setUpMyBusinessStyle}
                size="x-large"
                onClick={handleSetUpMyBusinessClicked}
              >
                {setupMyBusinessText}
              </Button>
            </Box>
          )}
          {isInCom966Test ? (
            <Button
              testID="header-link-login-signup-button"
              style={buttonStyle}
              onClick={() => promptUserForLoginOrSignup()}
            >
              Log In / Sign Up
            </Button>
          ) : (
            <React.Fragment>
              {showBusinessSetup && (
                <Button
                  testID="header-link-signup-button"
                  onClick={() => promptUserForLoginOrSignup('signup')}
                  style={buttonStyle}
                  size="large"
                >Sign Up
                </Button>
              )}
              <Button
                testID="header-link-login-button"
                onClick={() => promptUserForLoginOrSignup('login')}
                style={buttonStyle}
                size="large"
              >Log In
              </Button>
            </React.Fragment>
          )}

        </Fragment>
      )}
      {!providerId && (
        <Button
          testID="header-link-help-button"
          onClick={handleHelpClicked}
          style={buttonStyle}
          size="large"
        >
          Help
        </Button>
      )}

    </Inline>
  );
};
